import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/marketing-budget");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/marketing-budget")({
  head: () => ({
    meta: [
        { name: "description", content: "How much should a Colorado small business spend on marketing in 2026? Real benchmarks by industry, stage, and revenue from a Colorado agency owner." },
      {
        title: "Colorado Small Business Marketing Budget Guide 2026",
      },
      {
        name: "description",
        content:
          "Real percentage-of-revenue benchmarks by industry, plus a simple formula to figure out your own marketing budget.",
      },
      {
        property: "og:title",
        content: "Marketing Budget Benchmarks for Colorado Small Businesses",
      },
      {
        property: "og:description",
        content:
          "What you should actually spend on marketing in 2026 — by industry and stage.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/marketing-budget` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/marketing-budget" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Pricing & Budget"
      read="8 min read"
      title="How much should a Colorado small business spend on marketing in 2026?"
      intro="The two most common answers to this question are both wrong: &ldquo;7–10% of revenue&rdquo; (too vague) and &ldquo;whatever you can afford&rdquo; (too lazy). Here&rsquo;s the actual answer for Colorado small businesses."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Marketing budget" },
      ]}
      path="/learn/marketing-budget"
      faqs={[
        { q: "What percentage of revenue should a small business spend on marketing?", a: "Established Colorado service businesses typically spend 5–10% of revenue. Newer businesses or those in growth mode often spend 12–20%." },
        { q: "How do I split my marketing budget across SEO, PPC, and social?", a: "A common starting split is 50% SEO/website, 30% PPC, 20% social/reputation/email. Adjust based on how fast you need leads and how much organic traffic you already have." },
        { q: "How much is too little to spend on marketing?", a: "Below $500/month total is rarely enough to move the needle for a service business. You'll spread it too thin to do any one channel well." },
      ]}
    >
      <h2>The general benchmark (and where it breaks down)</h2>
      <p>
        Established small businesses typically spend <strong>5–12% of revenue</strong> on
        marketing. Growing businesses (hungry for share) push 15–20%. New businesses (fighting for
        their first 100 customers) often spend 20–30%.
      </p>
      <p>
        That&rsquo;s a useful starting point — but it ignores margin, customer lifetime value, and
        which channel you&rsquo;re using.
      </p>

      <h2>Benchmarks by industry (Colorado small business averages)</h2>
      <ul>
        <li><strong>HVAC / Plumbing / Electrical:</strong> 6–10% of revenue</li>
        <li><strong>Roofing / Restoration:</strong> 8–12%</li>
        <li><strong>Landscaping / Lawn care:</strong> 5–10%</li>
        <li><strong>Law firms (small):</strong> 8–15%</li>
        <li><strong>Medical / Dental:</strong> 4–8%</li>
        <li><strong>Restaurants:</strong> 3–6%</li>
        <li><strong>Retail / E-commerce:</strong> 8–15%</li>
        <li><strong>Auto repair:</strong> 5–9%</li>
      </ul>

      <h2>The smarter formula: cost per acquired customer</h2>
      <p>
        Forget percentage-of-revenue. Calculate this:
      </p>
      <ol>
        <li>What&rsquo;s your average customer worth in 12 months? (Average ticket × repeat rate)</li>
        <li>What gross margin do you make on that?</li>
        <li>How many of those new customers do you want to add this year?</li>
        <li>Your annual marketing budget = (new customers needed) × (max acceptable cost per customer).</li>
      </ol>
      <p>
        Most healthy small businesses can spend up to <strong>15–25% of a customer&rsquo;s
        first-year gross margin</strong> on marketing and still be profitable. If you&rsquo;re
        well under that, you&rsquo;re probably under-spending.
      </p>

      <h2>How to split that budget by channel</h2>
      <p>
        For most Colorado service businesses, a balanced 2026 split looks like:
      </p>
      <ul>
        <li><strong>30–40% — Local SEO + Google Maps</strong> (compounding asset)</li>
        <li><strong>25–40% — Google Ads / PPC</strong> (immediate fill)</li>
        <li><strong>10–15% — Website + creative</strong> (the conversion engine — see{" "}
          <Link to="/learn/website-conversion" className="font-bold text-brand-gold-deep underline">
            why websites don&rsquo;t convert
          </Link>
          )
        </li>
        <li><strong>5–10% — Reviews + reputation</strong></li>
        <li><strong>5–10% — Email / SMS to existing customers</strong></li>
        <li><strong>0–10% — Social, brand, sponsorships</strong></li>
      </ul>

      <h2>What this looks like in real Colorado dollars</h2>
      <ul>
        <li><strong>$500K/yr business:</strong> $25K–$60K/yr marketing budget = $2K–$5K/month.</li>
        <li><strong>$1M/yr business:</strong> $50K–$120K/yr = $4K–$10K/month.</li>
        <li><strong>$3M/yr business:</strong> $150K–$300K/yr = $12K–$25K/month.</li>
      </ul>

      <h2>Where most Colorado businesses waste budget</h2>
      <ul>
        <li>TV/radio/billboards before they&rsquo;ve maxed out search.</li>
        <li>&ldquo;Awareness&rdquo; campaigns with no tracking.</li>
        <li>Social media management for industries where customers don&rsquo;t buy from social.</li>
        <li>Six logo redesigns in three years.</li>
        <li>Cheap $99/month SEO that does literally nothing measurable.</li>
      </ul>

      <h2>Build your own number</h2>
      <p>
        See our{" "}
        <Link to="/pricing" className="font-bold text-brand-gold-deep underline">
          pricing page
        </Link>{" "}
        for the website and marketing packages and decide what
        fits your target monthly budget — no email gate. Or read our take on{" "}
        <Link to="/honest-answers/website-cost" className="font-bold text-brand-gold-deep underline">
          what a website really costs
        </Link>{" "}
        if you&rsquo;re budgeting your first build.
      </p>
    </ArticleLayout>
  );
}
